import type { DocumentRegion, LicenceClass, LicenceCondition, LicenceType } from './aiDocumentReader'
import type { INCOME_FREQUENCY } from './employmentStatus'
import type { PERSONAL_LOAN_PURPOSE } from './index'
import type { SUPPORTED_REGION } from './region'

export interface CommonDocumentData {
  // Customer name match
  declaredCustomerName: string | undefined
  [key: `detectedCustomerName${string}`]: string | null | undefined
  [key: `customerNameMatchPercentage${string}`]: number | null
}

export interface PayslipRuleData extends CommonDocumentData {
  // Employer match
  declaredEmployerName: string | undefined
  [key: `detectedEmployerName${string}`]: string | null | undefined
  [key: `employerNameMatchPercentage${string}`]: number | null

  // Recent payslip check
  [key: `startDate${string}`]: string | null | undefined
  [key: `endDate${string}`]: string | null | undefined
  [key: `incomeFrequency${string}`]: INCOME_FREQUENCY | null | undefined
  [key: `countDaysSince${string}End`]: number | null | undefined
  isConsecutiveDates: 0 | 1 | null

  // Employment status match
  declaredEmploymentStatus: string | undefined
  [key: `detectedEmploymentStatus${string}`]: string | null | undefined
  [key: `isEmploymentStatusMatch${string}`]: 0 | 1 | null
  [key: `ordinaryHoursOrUnits${string}`]: number | null | undefined
  [key: `isAnnualLeaveAccrued${string}`]: 0 | 1 | null
  [key: `isHolidayPayDetected${string}`]: 0 | 1 | null
  [key: `isAcePayslip${string}`]: 0 | 1 | null

  // Income type keyword flags
  [key: `isParentalLeave${string}`]: 0 | 1 | null
  [key: `isWorkersCompensation${string}`]: 0 | 1 | null

  // Income verification for payslip
  detectedIncomeFrequency: INCOME_FREQUENCY | null | undefined
  monthlyNetPrimaryIncome: number
  monthlyGrossPrimaryIncome: number

  // Tenure verification
  grossYTDIncomeByFrequency: number | null | undefined
  netYTDIncomePerFrequency: number | null | undefined
  [key: `netIncome${string}`]: number | null | undefined
  region: SUPPORTED_REGION

  // Deductions
  [key: `hasDeductions${string}`]: 0 | 1 | null
  [key: `deductionsValue${string}`]: number | null | undefined

  // Income data for dynamic RIPs
  avgPayslipNetIncomePerDeclaredFrequency: number | null
  lowestIncomePayslipName: string | null
  lowestPayslipNetIncomePerDeclaredFrequency: number | null
  netYTDIncomePerDeclaredFrequency: number | null
  grossYTDIncomePerDeclaredFrequency: number | null
}

export enum DOCUMENT_TYPE {
  DRIVERS_LICENCE = 'driversLicence',
  PASSPORT = 'passport',
  PROOF_OF_AGE_CARD = 'proofOfAgeCard',
}

export interface DriversLicenceRuleData {
  licenceNumber: string | null
  licenceClasses: LicenceClass[] | null
  carLicenceType: LicenceType | null
  motorbikeLicenceType: LicenceType | null
  carLicenceExpiryDate: string | null
  isCarLicenceExpired: 0 | 1 | null
  motorbikeLicenceExpiryDate: string | null
  isMotorbikeLicenceExpired: 0 | 1 | null
  licenceConditions: LicenceCondition[] | null
}

export interface PhotoIdRuleData extends CommonDocumentData, DriversLicenceRuleData {
  documentType: DOCUMENT_TYPE | null
  documentRegion: DocumentRegion | null
  isDigitalVersion: 0 | 1 | null
  loanPurpose: PERSONAL_LOAN_PURPOSE | 0
  declaredAddress: string | undefined
  detectedLatestAddress: string | null
  addressMatchPercentage: number | null
}

export interface TenancyAgreementRuleData {
  declaredRentPerMonth: number | null
  detectedRentPerMonth: number | null
  declaredNumTenantsOnTenancy: number | null
  detectedSignatoryCount: number | null
  declaredShareOfRentPerMonth: number | null
  expectedShareOfRentPerMonth: number | null
  detectedAddressTenancyAgreement: string | null
  addressMatchPercentageTenancyAgreement: number | null
}

export interface MedicareRuleData {
  detectedNumberOfIndividualsOnMedicareCard: number | null
  declaredNumberOfIndividualsInFamily: number | null
  isMedicareCardGreenAndNotInterim: number | null
  isMedicareCardExpired: number | null
}

export type DocumentData = PayslipRuleData & PhotoIdRuleData & TenancyAgreementRuleData & MedicareRuleData
